import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import WalletPositionsSection from '../components/funds/WalletPositionsSection';
import { formatFundAmount, sumWalletFields, tf } from '../constants/fundConstants';
import { useTranslation } from '../i18n/LocaleContext';

export default function WalletPage() {
  const { t } = useTranslation();
  const [wallet, setWallet] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [balanceVisible, setBalanceVisible] = useState(true);

  useEffect(() => {
    let cancelled = false;

    memberApi
      .getWallet()
      .then((data) => {
        if (!cancelled) {
          setWallet(data?.memberWallet || null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || t('funds.loadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  const summary = useMemo(() => sumWalletFields(wallet), [wallet]);

  function maskAmount(value) {
    return balanceVisible ? formatFundAmount(value) : '******';
  }

  return (
    <div className="funds-page funds-page--wallet">
      <header className="funds-page__header funds-page__header--center">
        <h1>{t('funds.title')}</h1>
      </header>

      {loading && <p className="funds-page__status">{t('common.loading')}</p>}
      {error && <p className="funds-page__error">{error}</p>}

      {!loading && !error && (
        <>
          <section className="funds-asset-card">
            <div className="funds-asset-card__head">
              <span>
                {tf(t, 'funds.totalAssets', { coin: summary.coinName })}
              </span>
              <button
                type="button"
                className="funds-asset-card__toggle"
                aria-label={balanceVisible ? t('funds.hideBalance') : t('funds.showBalance')}
                onClick={() => setBalanceVisible((v) => !v)}
              >
                {balanceVisible ? '👁' : '🙈'}
              </button>
            </div>
            <div className="funds-asset-card__balance">{maskAmount(summary.total)}</div>
            <dl className="funds-asset-card__stats">
              <div>
                <dt>{t('funds.available')}</dt>
                <dd>{maskAmount(summary.available)}</dd>
              </div>
              <div>
                <dt>{t('funds.locked')}</dt>
                <dd>{maskAmount(summary.frozen)}</dd>
              </div>
              <div>
                <dt>{t('funds.staked')}</dt>
                <dd>{maskAmount(summary.locked)}</dd>
              </div>
              <div>
                <dt>{t('funds.withdrawalStats')}</dt>
                <dd>{maskAmount(summary.withdrawalStats)}</dd>
              </div>
            </dl>
            <div className="funds-asset-card__actions">
              <Link to="/wallet/details" className="funds-asset-card__btn">
                {t('funds.details')}
              </Link>
              <Link to="/deposit" className="funds-asset-card__btn funds-asset-card__btn--primary">
                {t('funds.deposit')}
              </Link>
              <Link to="/withdraw" className="funds-asset-card__btn funds-asset-card__btn--primary">
                {t('funds.withdraw')}
              </Link>
            </div>
          </section>

          <WalletPositionsSection
            coinName={summary.coinName}
            balanceVisible={balanceVisible}
            walletBalance={summary.available}
          />
        </>
      )}
    </div>
  );
}
